import { useEffect, useCallback, useState } from 'react'

interface PreventWheelOptions {
  onScrollToBottom?: () => void
  onScrollToTop?: () => void
  /** Whether currently on the last page (disables forward navigation) */
  isLastPage?: boolean
  /** Whether currently on the first page (disables backward navigation) */
  isFirstPage?: boolean
}

/**
 * Prevents wheel events from propagating to parent containers (like tldraw canvas)
 * while maintaining correct scroll behavior within the container.
 *
 * Returns a callback ref to attach to the container element.
 * Uses native event listeners with { passive: false } to ensure preventDefault works.
 * Handles deltaMode to normalize scroll values across browsers (Firefox uses lines, not pixels).
 *
 * @param options - Optional callbacks for boundary scrolling (e.g., page navigation)
 * @example
 * const scrollRef = usePreventWheelPropagation()
 * return <div ref={scrollRef}>...</div>
 */
export function usePreventWheelPropagation(
  options?: PreventWheelOptions
): (node: HTMLElement | null) => void {
  const [container, setContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (!container) return

    // Scroll accumulation for page navigation
    let scrollAccumulator = 0
    let lastScrollTime = Date.now()
    let scrollResetTimeout: ReturnType<typeof setTimeout> | null = null
    const SCROLL_THRESHOLD = 150 // Pixels of scroll needed to trigger page change
    const SCROLL_RESET_MS = 200 // Reset accumulator if no scroll for this duration

    const handleWheel = (e: WheelEvent) => {
      // Let Ctrl+scroll pass through for zoom handling
      if (e.ctrlKey || e.metaKey) {
        e.stopPropagation()
        return
      }

      e.stopPropagation()
      e.preventDefault()

      // Convert delta to pixels based on deltaMode
      // deltaMode: 0 = DOM_DELTA_PIXEL, 1 = DOM_DELTA_LINE, 2 = DOM_DELTA_PAGE
      // Unknown/exotic values are treated as pixels (mode 0) for safety
      let deltaY = e.deltaY
      let deltaX = e.deltaX

      // Bounds check deltaMode - only valid values are 0, 1, 2
      // Treat any unexpected value as pixels (mode 0) to handle exotic input devices
      const deltaMode = e.deltaMode >= 0 && e.deltaMode <= 2 ? e.deltaMode : 0

      if (deltaMode === 1) {
        // DOM_DELTA_LINE - common in Firefox with certain mice
        deltaY *= 16 // approximate line height in pixels
        deltaX *= 16
      } else if (deltaMode === 2) {
        // DOM_DELTA_PAGE
        deltaY *= container.clientHeight
        deltaX *= container.clientWidth
      }
      // deltaMode === 0 (DOM_DELTA_PIXEL) - no conversion needed

      const now = Date.now()

      // Reset accumulator if too much time has passed (user stopped scrolling)
      if (now - lastScrollTime > SCROLL_RESET_MS) {
        scrollAccumulator = 0
      }
      lastScrollTime = now

      // Check if container has scrollable content
      const hasVerticalScroll = container.scrollHeight > container.clientHeight + 2

      if (hasVerticalScroll) {
        // Container has scrollable content - try scrolling first
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 5
        const isAtTop = container.scrollTop <= 5
        const scrollingDown = deltaY > 0
        const scrollingUp = deltaY < 0

        // If at boundary, accumulate scroll for page navigation
        if (scrollingDown && isAtBottom) {
          // Don't accumulate or navigate if already on the last page
          if (options?.isLastPage) {
            scrollAccumulator = 0
            return
          }
          scrollAccumulator += deltaY
          if (scrollAccumulator >= SCROLL_THRESHOLD && options?.onScrollToBottom) {
            scrollAccumulator = 0 // Reset after navigation
            container.scrollTop = 0 // Reset to top for next page
            options.onScrollToBottom()
          }
          return
        }

        if (scrollingUp && isAtTop) {
          // Don't accumulate or navigate if already on the first page
          if (options?.isFirstPage) {
            scrollAccumulator = 0
            return
          }
          scrollAccumulator += deltaY // deltaY is negative when scrolling up
          if (scrollAccumulator <= -SCROLL_THRESHOLD && options?.onScrollToTop) {
            scrollAccumulator = 0 // Reset after navigation
            options.onScrollToTop()
            // Clear any pending scroll reset
            if (scrollResetTimeout) {
              clearTimeout(scrollResetTimeout)
            }
            // Set scroll to bottom after page renders (with cleanup)
            scrollResetTimeout = setTimeout(() => {
              const maxScroll = container.scrollHeight - container.clientHeight
              if (maxScroll > 0) {
                container.scrollTop = maxScroll
              }
              scrollResetTimeout = null
            }, 100)
          }
          return
        }

        // Normal scrolling - reset accumulator and scroll within container
        scrollAccumulator = 0
        container.scrollTop += deltaY
        container.scrollLeft += deltaX
      } else {
        // No scrollable content - accumulate scroll to trigger page navigation
        const scrollingDown = deltaY > 0
        const scrollingUp = deltaY < 0

        // Don't accumulate or navigate at page boundaries
        if (scrollingDown && options?.isLastPage) {
          scrollAccumulator = 0
          return
        }
        if (scrollingUp && options?.isFirstPage) {
          scrollAccumulator = 0
          return
        }

        scrollAccumulator += deltaY

        if (scrollAccumulator >= SCROLL_THRESHOLD && options?.onScrollToBottom) {
          scrollAccumulator = 0
          container.scrollTop = 0
          options.onScrollToBottom()
        } else if (scrollAccumulator <= -SCROLL_THRESHOLD && options?.onScrollToTop) {
          scrollAccumulator = 0
          options.onScrollToTop()
          // Clear any pending scroll reset
          if (scrollResetTimeout) {
            clearTimeout(scrollResetTimeout)
          }
          // Set scroll to bottom after page renders
          scrollResetTimeout = setTimeout(() => {
            const maxScroll = container.scrollHeight - container.clientHeight
            if (maxScroll > 0) {
              container.scrollTop = maxScroll
            }
            scrollResetTimeout = null
          }, 100)
        }
      }
    }

    // Use non-passive listener to allow preventDefault
    container.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      container.removeEventListener('wheel', handleWheel)
      // Clean up any pending scroll reset timeout
      if (scrollResetTimeout) {
        clearTimeout(scrollResetTimeout)
      }
    }
  }, [container, options]) // Re-runs when container element or options change

  // Return callback ref - called by React when element mounts/unmounts
  return useCallback((node: HTMLElement | null) => {
    setContainer(node)
  }, [])
}
